import { type ReactNode } from 'react';
import { Card, Skeleton } from '../primitives';
import { EditorPropertyBar } from '../primitives/EditorPropertyBar';
import { AttributeRow } from './AttributeRow';
import { PropertyBarPillRow } from './PropertyBarPillRow';
import { DocumentTypePicker } from './DocumentTypePicker';
import { DocumentProjectPill } from './DocumentProjectPill';
import {
  useDocumentProperties,
  type DocumentPropertiesLoadState
} from './useDocumentProperties';
import type {
  CustomAttributeDefRow,
  CustomAttributeOptionRow
} from '../../lib/vm/useDocumentEditorVM';

export interface PropertiesPanelProps {
  documentId: string;
  orgId: string;
  typeId: string;
  editable: boolean;
  onError: (message: string) => void;
  /**
   * Persist a Type change. When provided, the panel renders the single "Type"
   * picker (pill) above the custom properties. Omit to hide the picker.
   */
  onSetType?: (typeId: string) => Promise<boolean>;
  load?: DocumentPropertiesLoadState;
  commitValue?: (
    propertyDefId: string,
    value: unknown
  ) => Promise<void>;
  createOption?: (
    propertyDefId: string,
    label: string
  ) => Promise<CustomAttributeOptionRow | null>;
  naked?: boolean;
  /** Canvas mount uses horizontal pills; inspector uses vertical rows. */
  variant?: 'bar' | 'panel';
  testIdPrefix?: string;
}

type PropertiesPanelInnerProps = Omit<
  PropertiesPanelProps,
  'load' | 'commitValue' | 'createOption'
> & {
  load: DocumentPropertiesLoadState;
  commitValue: (
    propertyDefId: string,
    value: unknown
  ) => Promise<void>;
  createOption: (
    propertyDefId: string,
    label: string
  ) => Promise<CustomAttributeOptionRow | null>;
};

export function PropertiesPanel(props: PropertiesPanelProps) {
  const { load, commitValue, createOption, ...rest } = props;
  if (load !== undefined && commitValue && createOption) {
    return (
      <PropertiesPanelInner
        {...rest}
        load={load}
        commitValue={commitValue}
        createOption={createOption}
      />
    );
  }
  return <PropertiesPanelWithHook {...rest} />;
}

function PropertiesPanelWithHook(
  props: Omit<PropertiesPanelProps, 'load' | 'commitValue' | 'createOption'>
) {
  const { load, commitValue, createOption } = useDocumentProperties({
    documentId: props.documentId,
    orgId: props.orgId,
    onError: props.onError
  });
  return (
    <PropertiesPanelInner
      {...props}
      load={load}
      commitValue={commitValue}
      createOption={createOption}
    />
  );
}

function PropertiesPanelInner({
  documentId,
  orgId,
  typeId,
  editable,
  onError,
  onSetType,
  naked = false,
  variant = 'panel',
  testIdPrefix = 'document-editor-v2-properties',
  load,
  commitValue,
  createOption
}: PropertiesPanelInnerProps) {
  const typePicker = onSetType ? (
    <DocumentTypePicker
      orgId={orgId}
      typeId={typeId}
      editable={editable}
      onSetType={onSetType}
      onError={onError}
      testId={`${testIdPrefix}-type`}
    />
  ) : null;

  const wrap = (state: 'loading' | 'error' | 'ready', body: ReactNode) => {
    if (variant === 'bar') {
      return (
        <div
          className="dev2-property-bar-canvas pn-editor-property-bar-host"
          data-testid={state === 'ready' ? undefined : testIdPrefix}
          data-state={state}
          aria-label="Document properties"
        >
          {body}
        </div>
      );
    }

    if (naked) {
      return (
        <div
          className="dev2-properties-naked"
          data-testid="document-editor-v2-properties"
          data-state={state}
          aria-label="Document properties"
        >
          {body}
        </div>
      );
    }
    return (
      <Card
        data-testid="document-editor-v2-properties"
        data-state={state}
        aria-label="Document properties"
      >
        <Card.Head title="Properties" />
        <Card.Body>{body}</Card.Body>
      </Card>
    );
  };

  if (load.kind === 'loading') {
    return wrap(
      'loading',
      <>
        <Skeleton width="100%" height={32} />
        <Skeleton width="100%" height={32} />
      </>
    );
  }

  if (load.kind === 'error') {
    return wrap(
      'error',
      <div className="dev2-properties-error">{load.message}</div>
    );
  }

  const body =
    variant === 'bar' ? (
      <CanvasPropertyBar
        documentId={documentId}
        orgId={orgId}
        typePicker={typePicker}
        load={load}
        editable={editable}
        testIdPrefix={testIdPrefix}
        onError={onError}
        commitValue={commitValue}
        createOption={createOption}
      />
    ) : (
      <InspectorPropertyRows
        typePicker={typePicker}
        load={load}
        editable={editable}
        testIdPrefix={testIdPrefix}
        commitValue={commitValue}
        createOption={createOption}
      />
    );

  return wrap('ready', body);
}

interface PropertyRowsProps {
  typePicker: ReactNode;
  load: Extract<DocumentPropertiesLoadState, { kind: 'ready' }>;
  editable: boolean;
  testIdPrefix: string;
  commitValue: (propertyDefId: string, value: unknown) => Promise<void>;
  createOption: (
    propertyDefId: string,
    label: string
  ) => Promise<CustomAttributeOptionRow | null>;
}

function CanvasPropertyBar({
  documentId,
  orgId,
  typePicker,
  load,
  editable,
  testIdPrefix,
  onError,
  commitValue,
  createOption
}: PropertyRowsProps & {
  documentId: string;
  orgId: string;
  onError: (message: string) => void;
}) {
  return (
    <EditorPropertyBar testIdPrefix={testIdPrefix} layout="scroll">
      {typePicker}
      <DocumentProjectPill
        documentId={documentId}
        orgId={orgId}
        editable={editable}
        testIdPrefix={testIdPrefix}
        onError={onError}
      />
      {load.defs.map((def) => (
        <PropertyBarPillRow
          key={def.key}
          def={def}
          value={load.values.find((v) => v.property_def_id === def.id)}
          isDefault={load.defaultPropIds.has(def.id)}
          selectOptions={load.optionsByDefId.get(def.id) ?? []}
          userOptions={load.userOptions}
          editable={editable}
          testIdPrefix={testIdPrefix}
          commitValue={commitValue}
          createOption={createOption}
        />
      ))}
    </EditorPropertyBar>
  );
}

function InspectorPropertyRows({
  typePicker,
  load,
  editable,
  testIdPrefix,
  commitValue,
  createOption
}: PropertyRowsProps) {
  return (
    <div className="dev2-properties-rows">
      {typePicker ? (
        <div
          className="dev2-properties-row"
          data-testid={`${testIdPrefix}-row-type`}
        >
          <span className="dev2-properties-marker" aria-hidden="true" />
          <div className="dev2-properties-row-body">{typePicker}</div>
        </div>
      ) : null}
      {load.defs.map((def) => {
        const value = load.values.find((v) => v.property_def_id === def.id);
        const isDefault = load.defaultPropIds.has(def.id);
        const selectOptions = load.optionsByDefId.get(def.id) ?? [];
        const compatDef = def as unknown as CustomAttributeDefRow;
        const compatOptions =
          selectOptions as unknown as CustomAttributeOptionRow[];
        return (
          <div
            key={def.id}
            className="dev2-properties-row"
            data-testid={`${testIdPrefix}-row-${def.key}`}
            data-default={isDefault ? '1' : '0'}
          >
            <span
              className="dev2-properties-marker"
              data-testid={`${testIdPrefix}-marker-${def.key}`}
              aria-hidden={!isDefault}
              aria-label={isDefault ? 'Subtype default property' : undefined}
            >
              {isDefault ? '✦' : ''}
            </span>
            <div className="dev2-properties-row-body">
              <AttributeRow
                def={compatDef}
                value={value}
                editable={editable}
                userOptions={load.userOptions}
                selectOptions={compatOptions}
                onCreateOption={createOption}
                onCommit={(_, v) => {
                  void commitValue(def.id, v);
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
